/**
 * Chip Pos File Converter
 *
 * Converts between chip_pos ASCII file format and ChipPosSelector component state.
 *
 * Chip_pos file format:
 * - Lines 1-6: Header metadata (C=, W=, X=, Y=, D=, F=)
 * - Line 7: Empty
 * - Lines 8+: ASCII wafer map where:
 *   - `-` = unmeasurable die
 *   - `+` = measurable die (not selected)
 *   - `+N` = selected die with measurement order N
 *   - `oN` = reference die (typically o0)
 */

/** Parsed header from a chip_pos file */
export type ChipPosHeader = {
  /** Configuration name (C=) */
  name: string;
  /** Wafer selection (W=) */
  waferSelection: string;
  /** Die width in µm (X=) */
  dieWidth: number;
  /** Die height in µm (Y=) */
  dieHeight: number;
  /** Wafer diameter in µm (D=) */
  diameter: number;
  /** Orientation (F=) */
  orientation: string;
};

/** A single die parsed from the ASCII map */
export type ParsedDie = {
  /** Column index relative to reference die */
  columnIndex: number;
  /** Row index relative to reference die */
  rowIndex: number;
  /** Whether the die is measurable */
  isMeasurable: boolean;
  /** Whether this is the reference die */
  isReference: boolean;
  /** Selection order (1-based), null if not selected */
  selectionOrder: number | null;
};

/** Complete parsed chip_pos data */
export type ParsedChipPos = {
  header: ChipPosHeader;
  dies: ParsedDie[];
  /** Die IDs in selection order (for ChipPosSelector's selectedDieIds prop) */
  selectedDieIds: string[];
  /** The reference die ID */
  referenceDieId: string | null;
};

/**
 * Parses a chip_pos file content into structured data.
 */
export function parseChipPosFile(content: string): ParsedChipPos {
  const lines = content.split("\n");

  // Parse header (lines 1-6)
  const header = parseHeader(lines.slice(0, 6));

  // Parse ASCII map (lines 8+, index 7+)
  const mapLines = lines.slice(7).filter((line) => line.trim().length > 0);
  const { dies, selectedDieIds, referenceDieId } = parseAsciiMap(mapLines);

  return {
    header,
    dies,
    selectedDieIds,
    referenceDieId,
  };
}

/**
 * Parses the header section of a chip_pos file.
 */
function parseHeader(lines: string[]): ChipPosHeader {
  const header: ChipPosHeader = {
    name: "",
    waferSelection: "",
    dieWidth: 0,
    dieHeight: 0,
    diameter: 0,
    orientation: "",
  };

  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.startsWith("C=")) {
      header.name = trimmed.slice(2).trim();
    } else if (trimmed.startsWith("W=")) {
      header.waferSelection = trimmed.slice(2).trim();
    } else if (trimmed.startsWith("X=")) {
      header.dieWidth = parseInt(trimmed.slice(2).trim(), 10) || 0;
    } else if (trimmed.startsWith("Y=")) {
      header.dieHeight = parseInt(trimmed.slice(2).trim(), 10) || 0;
    } else if (trimmed.startsWith("D=")) {
      header.diameter = parseInt(trimmed.slice(2).trim(), 10) || 0;
    } else if (trimmed.startsWith("F=")) {
      header.orientation = trimmed.slice(2).trim();
    }
  }

  return header;
}

/**
 * Token types in the ASCII map
 */
type MapToken = {
  type: "unmeasurable" | "measurable" | "selected" | "reference";
  order?: number;
};

/**
 * Tokenizes a single cell from the ASCII map.
 * Handles: -, +, +N, oN
 * @throws {Error} If the cell contains an invalid token
 */
function tokenizeCell(cell: string): MapToken | null {
  const trimmed = cell.trim();

  if (trimmed === "-") {
    return { type: "unmeasurable" };
  }

  if (trimmed === "+") {
    return { type: "measurable" };
  }

  // Check for +N (selected die with order)
  const selectedMatch = trimmed.match(/^\+(\d+)$/);
  if (selectedMatch) {
    return { type: "selected", order: parseInt(selectedMatch[1], 10) };
  }

  // Check for oN (reference die)
  const referenceMatch = trimmed.match(/^o(\d+)$/);
  if (referenceMatch) {
    return { type: "reference", order: parseInt(referenceMatch[1], 10) };
  }

  // Invalid token found - throw error for the user to handle
  throw new Error(`Invalid token in chip_pos file: "${trimmed}". See docs for supported format.`);
}

/**
 * Parses a line into tokens, handling whitespace correctly.
 * Two consecutive spaces "  " = one empty cell (first space is the cell, second is separator).
 * Single space between tokens = separator.
 */
function parseLineIntoTokens(line: string): (string | null)[] {
  const tokens: (string | null)[] = [];
  let i = 0;

  while (i < line.length) {
    if (line[i] === " ") {
      // Check if this is an empty cell (space followed by space or end of line)
      if (i + 1 >= line.length || line[i + 1] === " ") {
        // Empty cell: the space is the cell content
        tokens.push(null);
        i += 2; // Skip the empty cell space and separator (or just the space at end)
      } else {
        // Single space followed by non-space: this is just a separator
        i++;
      }
    } else {
      // Non-space: read until next space
      let end = i;
      while (end < line.length && line[end] !== " ") {
        end++;
      }
      tokens.push(line.slice(i, end));
      i = end; // Next iteration will handle the separator space
    }
  }

  return tokens;
}

/**
 * Parses the ASCII map lines into die data.
 */
function parseAsciiMap(lines: string[]): {
  dies: ParsedDie[];
  selectedDieIds: string[];
  referenceDieId: string | null;
} {
  // First pass: tokenize all cells and find the reference die position
  const grid: Array<Array<{ token: MapToken | null; gridCol: number; gridRow: number }>> = [];
  let refGridCol: number | null = null;
  let refGridRow: number | null = null;

  for (let rowIdx = 0; rowIdx < lines.length; rowIdx++) {
    const line = lines[rowIdx];
    // Parse line into tokens, handling "  " as empty cell
    const cells = parseLineIntoTokens(line);
    const rowTokens: Array<{ token: MapToken | null; gridCol: number; gridRow: number }> = [];

    for (let colIdx = 0; colIdx < cells.length; colIdx++) {
      const cell = cells[colIdx];
      const token = cell !== null ? tokenizeCell(cell) : null;

      rowTokens.push({ token, gridCol: colIdx, gridRow: rowIdx });

      if (token && token.type === "reference") {
        refGridCol = colIdx;
        refGridRow = rowIdx;
      }
    }
    grid.push(rowTokens);
  }

  // If no reference die found, use center of grid
  if (refGridCol === null || refGridRow === null) {
    // Find grid bounds
    let maxCol = 0;
    for (const row of grid) {
      for (const cell of row) {
        maxCol = Math.max(maxCol, cell.gridCol);
      }
    }
    refGridCol = Math.floor(maxCol / 2);
    refGridRow = Math.floor(grid.length / 2);
  }

  // Second pass: create dies with positions relative to reference
  const dies: ParsedDie[] = [];
  // Track selected dies (non-reference) separately
  const selectedDies: Array<{ id: string; order: number }> = [];
  let referenceDieId: string | null = null;

  for (const row of grid) {
    for (const { token, gridCol, gridRow } of row) {
      // Skip empty cells (null tokens)
      if (!token) {
        continue;
      }

      // Calculate position relative to reference die
      // Note: In ASCII, row 0 is top, but in ChipPosSelector, positive Y is down
      // So we keep the same convention
      const columnIndex = gridCol - refGridCol;
      const rowIndex = gridRow - refGridRow;
      const id = `${columnIndex}:${rowIndex}`;

      // Handle unmeasurable dies
      if (token.type === "unmeasurable") {
        dies.push({
          columnIndex,
          rowIndex,
          isMeasurable: false,
          isReference: false,
          selectionOrder: null,
        });
        continue;
      }

      const die: ParsedDie = {
        columnIndex,
        rowIndex,
        isMeasurable: true, // All dies that reach here are measurable
        isReference: token.type === "reference",
        selectionOrder: null,
      };

      if (token.type === "selected" && token.order !== undefined) {
        die.selectionOrder = token.order;
        selectedDies.push({ id, order: token.order });
      }

      if (token.type === "reference") {
        referenceDieId = id;
        // Reference die is always order 0 (first to be measured)
        die.selectionOrder = 0;
        // Note: reference die is handled separately, not added to selectedDies here
      }

      dies.push(die);
    }
  }

  // Sort selected dies by order (excluding reference die which is always first)
  selectedDies.sort((a, b) => a.order - b.order);

  // Reference die is always first in selection (order 0), followed by other selected dies
  const selectedDieIds = referenceDieId
    ? [referenceDieId, ...selectedDies.map((d) => d.id)]
    : selectedDies.map((d) => d.id);

  return { dies, selectedDieIds, referenceDieId };
}

/**
 * Converts parsed chip_pos data back to chip_pos file format.
 */
export function toChipPosFile(parsed: ParsedChipPos): string {
  const { header, dies, selectedDieIds } = parsed;

  // Build header
  const headerLines = [
    `C= ${header.name}`,
    `W= ${header.waferSelection}`,
    `X= ${header.dieWidth}`,
    `Y= ${header.dieHeight}`,
    `D= ${header.diameter}`,
    `F= ${header.orientation}`,
    "", // Empty line between header and map
  ];

  // Create selection order map
  const selectionOrderMap = new Map<string, number>();
  selectedDieIds.forEach((id, index) => {
    selectionOrderMap.set(id, index);
  });

  // Find grid bounds
  let minCol = Infinity;
  let maxCol = -Infinity;
  let minRow = Infinity;
  let maxRow = -Infinity;

  for (const die of dies) {
    minCol = Math.min(minCol, die.columnIndex);
    maxCol = Math.max(maxCol, die.columnIndex);
    minRow = Math.min(minRow, die.rowIndex);
    maxRow = Math.max(maxRow, die.rowIndex);
  }

  // Create die lookup map
  const dieMap = new Map<string, ParsedDie>();
  for (const die of dies) {
    const id = `${die.columnIndex}:${die.rowIndex}`;
    dieMap.set(id, die);
  }

  // Build ASCII map
  const mapLines: string[] = [];

  for (let row = minRow; row <= maxRow; row++) {
    const rowCells: string[] = [];

    for (let col = minCol; col <= maxCol; col++) {
      const id = `${col}:${row}`;
      const die = dieMap.get(id);

      if (!die) {
        // No die at this position - use space for padding
        rowCells.push(" ");
        continue;
      }

      // Check if die is measurable
      if (!die.isMeasurable) {
        rowCells.push("-");
        continue;
      }

      // Check if die is selected
      const selectionOrder = selectionOrderMap.get(id);

      // Reference die is always o0 (first to be measured)
      if (die.isReference) {
        rowCells.push("o0");
      } else if (selectionOrder !== undefined) {
        // Non-reference selected dies use +N format
        // Since reference die is always first (index 0), other dies start from index 1
        rowCells.push(`+${selectionOrder}`);
      } else {
        rowCells.push("+");
      }
    }

    // Trim trailing spaces but keep structure
    mapLines.push(rowCells.join(" "));
  }

  return [...headerLines, "\n", ...mapLines].join("\n");
}

/**
 * Converts parsed chip_pos data to ChipPosSelector props.
 */
export function toChipPosSelectorProps(parsed: ParsedChipPos): {
  diameter: number;
  dieWidth: number;
  dieHeight: number;
  selectedDieIds: string[];
} {
  return {
    // Convert diameter from µm to mm
    diameter: parsed.header.diameter / 1000,
    dieWidth: parsed.header.dieWidth,
    dieHeight: parsed.header.dieHeight,
    selectedDieIds: parsed.selectedDieIds,
  };
}

/**
 * Creates a set of measurable die IDs from parsed data.
 * This can be used to track which dies are measurable vs unmeasurable.
 */
export function getMeasurableDieIds(parsed: ParsedChipPos): Set<string> {
  const measurable = new Set<string>();
  for (const die of parsed.dies) {
    if (die.isMeasurable) {
      measurable.add(`${die.columnIndex}:${die.rowIndex}`);
    }
  }
  return measurable;
}

/**
 * Creates a set of unmeasurable die IDs from parsed data.
 */
export function getUnmeasurableDieIds(parsed: ParsedChipPos): Set<string> {
  const unmeasurable = new Set<string>();
  for (const die of parsed.dies) {
    if (!die.isMeasurable) {
      unmeasurable.add(`${die.columnIndex}:${die.rowIndex}`);
    }
  }
  return unmeasurable;
}
